import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlarmClock,
  AlertTriangle,
  Building2,
  Download,
  FlaskConical,
  Sparkles,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useState, type JSX } from 'react';

import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import {
  getSubscriptionExport,
  subscriptionsToCsv,
} from '@/features/tenants/api/subscription.graphql';
import { useModuleCatalog } from '@/features/tenants/hooks/useModuleCatalog';
import { downloadText } from '@/lib/csv';
import { formatMoney } from '@/lib/money';

import styles from './DashboardPage.module.scss';
import {
  getPlatformSummary,
  getTenantHealthScores,
  type PlatformSummary,
} from '../api/metrics.graphql';
import { AttentionPanel } from '../components/AttentionPanel';
import { TenantOverview } from '../components/TenantOverview';

/**
 * OS-1723 — MRR real por módulo y exportación CSV de suscripciones. Los montos vienen del mismo
 * cálculo que el KPI: la suma de esta tabla es igual al MRR real.
 */
function MrrByModulePanel({ summary }: { summary: PlatformSummary }): JSX.Element {
  const catalog = useModuleCatalog();
  const names = new Map((catalog.data ?? []).map((module) => [module.key, module.name]));
  const [exportError, setExportError] = useState<string | null>(null);
  const exporter = useMutation({
    mutationFn: getSubscriptionExport,
    onSuccess: (rows) => {
      setExportError(null);
      const date = new Date().toISOString().slice(0, 10);
      downloadText(`suscripciones-${date}.csv`, subscriptionsToCsv(rows));
    },
    onError: (error) => setExportError(error.message),
  });
  const rows = summary.mrrByModule ?? [];

  return (
    <section className={styles.section} aria-labelledby="mrr-by-module-title">
      <div className={styles.sectionHeader}>
        <h2 id="mrr-by-module-title" className={styles.sectionTitle}>
          MRR por módulo
        </h2>
        <button
          type="button"
          className={styles.exportBtn}
          onClick={() => exporter.mutate()}
          disabled={exporter.isPending}
        >
          <Download size={14} aria-hidden="true" />
          {exporter.isPending ? 'Exportando…' : 'Exportar suscripciones (CSV)'}
        </button>
      </div>
      {exportError ? (
        <p className={styles.exportError} role="alert">
          No se pudo exportar: {exportError}
        </p>
      ) : null}
      <div className={styles.tableWrapper}>
        <table className={styles.table} aria-labelledby="mrr-by-module-title">
          <thead>
            <tr>
              <th scope="col">Módulo</th>
              <th scope="col">MRR ({summary.currency})</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className={styles.emptyState}>
                  Ningún tenant activo tiene módulos en su suscripción.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.moduleKey}>
                  <td>{names.get(row.moduleKey) ?? row.moduleKey}</td>
                  <td>{formatMoney(row.mrr, summary.currency)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function KpiTile({
  label,
  value,
  sub,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  accent?: 'success' | 'warning' | 'danger';
}): JSX.Element {
  return (
    <div className={`${styles.kpiTile} ${accent ? styles[accent] : ''}`}>
      <div className={styles.kpiIcon}>
        <Icon size={18} />
      </div>
      <div className={styles.kpiBody}>
        <div className={styles.kpiValue}>{value}</div>
        <div className={styles.kpiLabel}>{label}</div>
        {sub && <div className={styles.kpiSub}>{sub}</div>}
      </div>
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const { can } = usePlatformPermissions();
  const summaryQ = useQuery({
    queryKey: ['platform-summary'],
    queryFn: getPlatformSummary,
    refetchInterval: 5 * 60 * 1000,
  });

  // OS-1724 — salud de todos los tenants (una sola consulta para KPI, atención y panorama).
  const canReports = can('platform.reports.read');
  const healthQ = useQuery({
    queryKey: ['tenant-health-scores'],
    queryFn: () => getTenantHealthScores(),
    refetchInterval: 5 * 60 * 1000,
    enabled: canReports,
  });

  const s = summaryQ.data;
  const health = healthQ.data ?? [];
  const highRisk = health.filter((t) => t.churnRisk === 'high');
  const withoutData = health.filter((t) => t.churnRisk === 'no_data').length;

  return (
    <div className={styles.page}>
      <div className={styles.kpiGrid}>
        <KpiTile
          label="Activos (pago)"
          value={s?.activeTenants ?? '—'}
          sub={s ? `${s.totalTenants} total` : undefined}
          icon={Building2}
          accent="success"
        />
        {/* OS-1720 — por estado del ciclo de vida, no por plan. */}
        <KpiTile
          label="En piloto"
          value={s?.pilotTenants ?? '—'}
          sub={s && s.trialTenants > 0 ? `${s.trialTenants} en trial (previos)` : undefined}
          icon={FlaskConical}
        />
        <KpiTile
          label="En mora"
          value={s?.pastDueTenants ?? '—'}
          sub={s ? `${s.suspendedTenants} suspendidos` : undefined}
          icon={AlarmClock}
          accent={s && s.pastDueTenants > 0 ? 'warning' : undefined}
        />
        {/* OS-1717 — el API omite el MRR para roles sin facturación (SOPORTE). */}
        {/* OS-1723 — MRR calculado de las líneas de suscripción, en COP. */}
        {s && s.totalMrr === null ? null : (
          <>
            <KpiTile
              label="MRR real"
              value={formatMoney(s?.totalMrr, s?.currency)}
              sub="activos y en mora"
              icon={TrendingUp}
            />
            <KpiTile
              label="MRR potencial"
              value={formatMoney(s?.potentialMrr, s?.currency)}
              sub="pilotos (aún no suma)"
              icon={Sparkles}
            />
            <KpiTile
              label="ARPA"
              value={formatMoney(s?.arpa, s?.currency)}
              sub={s && s.payingTenants !== null ? `${s.payingTenants} tenants que pagan` : undefined}
              icon={Wallet}
            />
          </>
        )}
        <KpiTile
          label="Alto riesgo de churn"
          value={healthQ.data ? highRisk.length : '—'}
          sub={
            withoutData > 0
              ? `últimos 30 días · ${withoutData} sin datos de uso`
              : 'últimos 30 días'
          }
          icon={AlertTriangle}
          accent={highRisk.length > 0 ? 'danger' : undefined}
        />
      </div>

      {/* OS-1724 — un solo panel consolidado: lo que hay que atender hoy y el panorama de tenants. */}
      <AttentionPanel
        health={healthQ.data}
        healthLoading={healthQ.isLoading}
        healthError={healthQ.error}
      />
      {can('platform.tenants.read') ? <TenantOverview health={healthQ.data} /> : null}
      {s && s.totalMrr !== null ? <MrrByModulePanel summary={s} /> : null}
    </div>
  );
}
